import { useEffect, useState } from "react";
import { FaCalendarCheck, FaHandshakeSimple, FaUser } from "react-icons/fa6";
import { PromiseToPayService } from "../../../../../services/PromiseToPayService";
import { SelectPppoeUser } from "../../../../../components/forms/selects/SelectPppoeUser";
import { TextAreaDefault } from "../../../../../components/forms/TextAreaDefault";
import { ModalWraper } from "../../../../../components/modal/wraper";

export const CreateNewPromiseToPayModal = ({
    refresh,
    openModal,
    setOpenModal,
    hidePppoeUser = false,
    pppoeUserId,
}) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        pppoe_user_id: null,
        date: "",
        description: ""
    });

    useEffect(() => {
        if (pppoeUserId) {
            setFormData((prev) => ({
                ...prev,
                pppoe_user_id: pppoeUserId,
            }));
        }
    }, [pppoeUserId]);

    const closeModal = () => {
        setOpenModal(false);
        setFormData({
            pppoe_user_id: null,
            date: "",
            description: ""
        });
        setErrors({});
        setLoading(false);
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.pppoe_user_id) {
            newErrors.pppoe_user_id = "Pelanggan harus dipilih";
        }

        if (!formData.date) {
            newErrors.date = "Tanggal janji bayar harus diisi";
        } else {
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            selectedDate.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                newErrors.date = "Tanggal tidak boleh kurang dari hari ini";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);
            setErrors({});

            await PromiseToPayService.store({
                data: {
                    pppoe_user_id: formData.pppoe_user_id,
                    date: formData.date,
                    description: formData.description
                },
            });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            console.error("Gagal menyimpan janji bayar:", err);

            const serverErrors = err?.response?.data;

            if (typeof serverErrors === "object" && serverErrors !== null) {
                // Jika error dari validator Laravel (format: { field: ["message"] })
                const formatted = {};
                for (const [key, value] of Object.entries(serverErrors)) {
                    formatted[key] = Array.isArray(value) ? value[0] : value;
                }
                setErrors(formatted);
            } else if (serverErrors?.message) {
                // Jika error berupa { message: "..." }
                setErrors({ general: serverErrors.message });
            } else {
                setErrors({ general: "Terjadi kesalahan saat menyimpan data" });
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper: dapatkan tanggal hari ini dalam format YYYY-MM-DD untuk min attribute
    const todayStr = new Date().toISOString().split("T")[0];

    return (
        <ModalWraper
            isOpen={openModal}
            setIsOpen={setOpenModal}
            title="Tambah Janji Bayar"
            icon={<FaHandshakeSimple className="text-cyan-500" />}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error General */}
                {errors.general && (
                    <div
                        className="px-4 py-3 rounded-xl text-sm text-red-400 border border-red-500/20"
                        style={{ background: "rgba(239, 68, 68, 0.06)" }}
                    >
                        {errors.general}
                    </div>
                )}

                {/* Pilih Pelanggan */}
                {!hidePppoeUser && (
                    <div>
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                            <FaUser className="text-[10px]" />
                            Pilih Pelanggan
                        </label>
                        <SelectPppoeUser
                            value={formData.pppoe_user_id}
                            onChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    pppoe_user_id: value?.id || null,
                                }))
                            }
                        />
                        {errors.pppoe_user_id && (
                            <p className="text-xs text-red-400 mt-1.5">
                                {errors.pppoe_user_id}
                            </p>
                        )}
                    </div>
                )}

                {/* Tanggal Janji Bayar */}
                <div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                        <FaCalendarCheck className="text-[10px]" />
                        Tanggal Janji Bayar
                    </label>
                    <input
                        type="date"
                        value={formData.date}
                        min={todayStr}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                date: e.target.value,
                            }))
                        }
                        className={`w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border text-slate-100 outline-none transition-all focus:ring-1 ${errors.date
                            ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/20"
                            : "border-slate-700 focus:border-cyan-500 focus:ring-cyan-500/20"
                            }`}
                        style={{
                            colorScheme: "dark",
                        }}
                    />
                    {errors.date && (
                        <p className="text-xs text-red-400 mt-1.5">
                            {errors.date}
                        </p>
                    )}
                </div>

                <TextAreaDefault
                    label="Deskripsi"
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Deskripsi janji bayar"
                    error={errors.description}
                />

                {/* Info Ringkasan */}
                {formData.pppoe_user_id && formData.date && (
                    <div
                        className="rounded-xl p-4 space-y-2"
                        style={{
                            background: "rgba(0, 180, 216, 0.04)",
                            border: "1px solid rgba(0, 180, 216, 0.1)",
                        }}
                    >
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Ringkasan
                        </p>
                        <p className="text-sm text-slate-300">
                            Pelanggan terpilih akan memiliki janji bayar pada{" "}
                            <span className="font-semibold text-cyan-400">
                                {new Date(formData.date).toLocaleDateString("id-ID", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </span>
                        </p>
                    </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 font-medium transition-colors hover:bg-slate-800"
                    >
                        Batal
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <>
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <FaCalendarCheck className="text-sm" />
                                Simpan
                            </>
                        )}
                    </button>
                </div>
            </form>
        </ModalWraper>
    );
};